import React, { useState, useEffect } from 'react';
import openaiService, { OPENAI_MODELS } from '../services/openaiService';
import openaiUsageService from '../services/openaiUsageService';
import { toast } from 'react-toastify';

const OpenAIModelSelector = () => {
  const [config, setConfig] = useState({
    modelName: '',
    maxTokens: 2000,
    temperature: 0.7,
    endpoint: 'https://api.openai.com/v1/'
  });
  const [selectedModel, setSelectedModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  
  // Estados para el monitoreo de uso
  const [usageView, setUsageView] = useState('current'); // 'current', 'monthly', 'range'
  const [currentUsage, setCurrentUsage] = useState(null);
  const [monthlyUsage, setMonthlyUsage] = useState(null);
  const [rangeUsage, setRangeUsage] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Cargar la configuración actual al montar el componente
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const configData = await openaiService.getConfig();
        setConfig(configData);
        setSelectedModel(configData.modelName);
      } catch (error) {
        console.error('Error al cargar la configuración de OpenAI:', error);
        toast.error('No se pudo cargar la configuración de OpenAI');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Cargar tasa de cambio al montar el componente
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const rate = await openaiUsageService.getExchangeRate();
        setExchangeRate(rate);
      } catch (error) {
        console.error('Error al cargar tasa de cambio:', error);
      }
    };

    fetchExchangeRate();
  }, []);

  // Cargar datos de uso según la vista seleccionada
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoadingUsage(true);
        
        if (usageView === 'current') {
          const data = await openaiUsageService.getCurrentUsage();
          setCurrentUsage(data);
        } else if (usageView === 'monthly') {
          const data = await openaiUsageService.getMonthlyUsage();
          setMonthlyUsage(data);
        }
      } catch (error) {
        console.error('Error al cargar datos de uso:', error);
        toast.error('No se pudieron cargar los datos de uso');
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, [usageView]);

  // Función para cambiar el modelo
  const handleChangeModel = async () => {
    if (config.modelName === selectedModel) {
      toast.info('Ya estás usando este modelo');
      return;
    }

    try {
      setLoading(true);
      await openaiService.updateModel(selectedModel);
      
      // Volver a cargar la configuración completa para asegurar consistencia
      const updatedConfig = await openaiService.getConfig();
      setConfig(updatedConfig);
      
      toast.success(`Modelo cambiado a ${getModelName(selectedModel)}`);
      setIsChanging(false);
    } catch (error) {
      console.error('Error al cambiar el modelo:', error);
      toast.error('No se pudo cambiar el modelo de OpenAI');
    } finally {
      setLoading(false);
    }
  };

  // Obtener el nombre amigable del modelo
  const getModelName = (modelId) => {
    const model = OPENAI_MODELS.find(m => m.id === modelId);
    return model ? model.name : modelId;
  };

  // Función para buscar uso por rango de fechas
  const handleSearchRange = async () => {
    if (!startDate || !endDate) {
      toast.warning('Por favor selecciona ambas fechas');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    try {
      setLoadingUsage(true);
      const data = await openaiUsageService.getRangeUsage(startDate, endDate);
      setRangeUsage(data);
    } catch (error) {
      console.error('Error al buscar uso por rango:', error);
      toast.error('No se pudieron cargar los datos del rango seleccionado');
    } finally {
      setLoadingUsage(false);
    }
  };

  // Función para formatear números
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('es-CO').format(num);
  };

  // Función para formatear moneda
  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '$0';
    const decimals = currency === 'USD' ? 5 : 2;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Configuración del Modelo */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Configuración de OpenAI</h3>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Modelo actual:</p>
          <div className="flex items-center">
            <span className="bg-blue-100 text-blue-800 py-1 px-3 rounded-full font-medium">
              {loading ? 'Cargando...' : getModelName(config.modelName)}
            </span>
            {!isChanging && (
              <button 
                onClick={() => setIsChanging(true)}
                className="ml-4 text-blue-600 hover:text-blue-800 text-sm"
              >
                Cambiar modelo
              </button>
            )}
          </div>
        </div>
        
        {isChanging && (
          <div className="mt-4 border-t pt-4">
            <p className="text-sm text-gray-600 mb-2">Selecciona un nuevo modelo:</p>
            
            <div className="mb-4">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                {OPENAI_MODELS.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={handleChangeModel}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Actualizando...
                  </>
                ) : 'Actualizar modelo'}
              </button>
              <button
                onClick={() => setIsChanging(false)}
                disabled={loading}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Monitoreo de Uso de Tokens */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Monitoreo de Uso de API</h3>
        
        {/* Tasa de Cambio */}
        {exchangeRate && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Tasa de cambio: <span className="font-semibold text-blue-700">
                1 USD = {formatNumber(exchangeRate.rate || exchangeRate)} COP
              </span>
            </p>
          </div>
        )}

        {/* Pestañas de Vista */}
        <div className="flex space-x-2 mb-4 border-b">
          <button
            onClick={() => setUsageView('current')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              usageView === 'current'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Uso de Hoy
          </button>
          <button
            onClick={() => setUsageView('monthly')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              usageView === 'monthly'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Uso del Mes
          </button>
          <button
            onClick={() => setUsageView('range')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              usageView === 'range'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Rango de Fechas
          </button>
        </div>

        {/* Contenido según la vista seleccionada */}
        {loadingUsage ? (
          <div className="flex justify-center items-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <>
            {/* Vista de Uso Actual */}
            {usageView === 'current' && currentUsage && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Costo en USD</p>
                    <p className="text-3xl font-bold text-green-700">
                      {formatCurrency(currentUsage.costUSD, 'USD')}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Costo en COP</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {formatCurrency(currentUsage.costCOP, 'COP')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">
                    Período: {currentUsage.period || 'Diario'} | {currentUsage.startDate ? new Date(currentUsage.startDate).toLocaleDateString('es-CO') : 'Hoy'}
                  </p>
                </div>
              </div>
            )}

            {/* Vista de Uso Mensual */}
            {usageView === 'monthly' && monthlyUsage && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Costo en USD</p>
                    <p className="text-3xl font-bold text-green-700">
                      {formatCurrency(monthlyUsage.costUSD, 'USD')}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Costo en COP</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {formatCurrency(monthlyUsage.costCOP, 'COP')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">
                    Período: {monthlyUsage.period || 'Mensual'} | {monthlyUsage.startDate ? new Date(monthlyUsage.startDate).toLocaleDateString('es-CO') : 'Mes actual'}
                  </p>
                </div>
              </div>
            )}

            {/* Vista de Rango de Fechas */}
            {usageView === 'range' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Inicio
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Fin
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSearchRange}
                  disabled={loadingUsage}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Buscar
                </button>

                {rangeUsage && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-600 mb-2">Costo en USD</p>
                        <p className="text-3xl font-bold text-green-700">
                          {formatCurrency(rangeUsage.costUSD, 'USD')}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-600 mb-2">Costo en COP</p>
                        <p className="text-3xl font-bold text-blue-700">
                          {formatCurrency(rangeUsage.costCOP, 'COP')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">
                        Rango: {new Date(startDate).toLocaleDateString('es-CO')} - {new Date(endDate).toLocaleDateString('es-CO')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OpenAIModelSelector;
